import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {Checkbox, Modal, Text} from '@forecast-it/design-system';

import ForecastQueryRenderer from '../../../../ForecastQueryRenderer';
import {RelayProps} from '../../../../relay-helpers/TSRelayConverter';
import {isDoneTaskTimeRegistrationAllowed} from '../../../shared/util/cache/TimeRegistrationSettingsUtil';
import {dateRangeToYMD} from '../../../shared/util/DateUtil';
import {CopyLastWeekValidationSection_viewer} from './__generated__/CopyLastWeekValidationSection_viewer.graphql';
import CopyLastWeekValidationSection, {CopyLastWeekValidationSectionQuery} from './CopyLastWeekValidationSection';
import {TimesheetsCopyLastWeekModalFooter} from './TimesheetsCopyLastWeekModalFooter';
import {TimesheetsCopyLastWeekModalLoader} from './TimesheetsCopyLastWeekModalLoader';
import {dateRangeToText, getCopyFromDateRange, getCopyToDateRange} from './TimesheetsCopyLastWeekModalLogic';

type TimesheetsCopyLastWeekModalProps = {
	personId: string;
	selectedDate: Date;
	copyFullWeek: boolean; // Toggles whether the previous week should be copied, or only the previous day
	closeModal: () => void;
};

/**
 * Modal for copying last week's timesheets
 * @param closeModal - function to close the modal
 */
export function TimesheetsCopyLastWeekModal({
	personId,
	selectedDate,
	copyFullWeek,
	closeModal,
}: TimesheetsCopyLastWeekModalProps): JSX.Element {
	const [mutationSent, setMutationSent] = useState(false);
	const [copyDoneTasks, setCopyDoneTasks] = useState<boolean>(false);

	const timeRegsCount = useRef<number>(0);

	const {formatMessage} = useIntl();

	const onCopyCallback = () => {
		setMutationSent(true);
	};

	const copyFromDateRange = getCopyFromDateRange(selectedDate, copyFullWeek);
	const copyToDateRange = getCopyToDateRange(selectedDate, copyFullWeek);
	const doneTaskTimeRegistrationAllowed = isDoneTaskTimeRegistrationAllowed();

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>
				{copyFullWeek
					? formatMessage({id: 'timesheets.copy_previous_week'})
					: formatMessage({id: 'timesheets.copy_previous_day'})}
			</Modal.Title>
			{mutationSent ? (
				<TimesheetsCopyLastWeekModalLoader />
			) : (
				<>
					<Modal.Content>
						<Text>
							{`Time registrations from `}
							<b>{dateRangeToText(copyFromDateRange)}</b>
							{` will be added to the `}
							<b>{dateRangeToText(copyToDateRange)}</b>
							{` timesheet.`}
						</Text>
						<ForecastQueryRenderer
							key="copy-last-week-validation-section"
							query={CopyLastWeekValidationSectionQuery}
							variables={{
								personId,
								...dateRangeToYMD(copyFromDateRange),
							}}
							showLoader={false}
							render={(relayProps: RelayProps<CopyLastWeekValidationSection_viewer>) => (
								<CopyLastWeekValidationSection
									{...relayProps}
									copyDoneTasks={copyDoneTasks}
									timeRegsCount={timeRegsCount}
								/>
							)}
						/>
						{doneTaskTimeRegistrationAllowed && (
							<>
								<Text>{`Do you want to include time registrations on Done tasks as well?`}</Text>
								<Checkbox
									checked={copyDoneTasks}
									onChange={checked => setCopyDoneTasks(!!checked)}
									labelText={'Include time registrations on Done tasks'}
									notBoldLabel={true}
									data-cy="include-done-checkbox"
								/>
							</>
						)}
					</Modal.Content>
					<TimesheetsCopyLastWeekModalFooter
						personId={personId}
						copyDateRange={copyFromDateRange}
						closeModal={closeModal}
						onCopyCallback={onCopyCallback}
						copyDone={copyDoneTasks}
						copyFullWeek={copyFullWeek}
					/>
				</>
			)}
		</Modal>
	);
}
